'use client';

import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Eye,
  Lock,
  Code2,
  Activity,
  Gauge,
  ScanSearch,
} from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { platformLink } from '@/lib/config';
import { organizationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const capabilities = [
  {
    icon: Activity,
    title: 'Inline detection in the SDK',
    body: 'The SDK inspects traffic in-process and flags SQL injection, XSS, prompt injection, PII/secret leaks, and abusive rate patterns - with ~0ms added latency.',
  },
  {
    icon: Gauge,
    title: 'Platform & dashboards',
    body: 'The platform turns telemetry into dashboards and analytics, with remote configuration that updates the running SDK on the next sync - no redeploy needed.',
  },
  {
    icon: ScanSearch,
    title: 'AI verification & pentests',
    body: 'On-demand AI threat verification reduces false positives, and pentests give you a deeper, point-in-time view of your real exposure.',
  },
];

const principles = [
  {
    icon: Eye,
    title: 'Transparency',
    body: 'We publish our detection benchmarks openly - including what we miss. No cherry-picked numbers.',
    href: '/benchmarks',
    cta: 'See the benchmarks',
  },
  {
    icon: Lock,
    title: 'Security-first',
    body: 'Hashed API keys, row-level isolation per tenant, and PII sanitization client-side before any telemetry leaves your servers.',
    href: '/security',
    cta: 'How we protect data',
  },
  {
    icon: Code2,
    title: 'Developer-first',
    body: 'One npm install, zero-config defaults, and no vendor lock-in. It runs where your app runs.',
    href: '/docs',
    cta: 'Read the docs',
  },
];

const aboutBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'About', url: `${SITE_URL}/about` },
]);

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
            Company
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              Secure the web that AI is building
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl leading-relaxed">
            AI-generated apps and LLM features ship faster than security can keep up. Silker AI provides
            runtime protection that installs in minutes - detecting and blocking threats inline, where your
            application actually runs.
          </p>
        </div>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 mb-12">
          <h2 className="text-2xl font-semibold text-white mb-3">Our mission</h2>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Every new wave of software creates a new attack surface, and AI has dramatically accelerated how
            much software gets shipped. Code is written faster, LLM features are everywhere, and the gap
            between &quot;deployed&quot; and &quot;secured&quot; keeps widening. We believe runtime security should be the
            default, not an afterthought - so we built protection that drops into an existing app in minutes
            and starts catching real attacks immediately.
          </p>
        </section>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6">What we do</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {capabilities.map(({ icon: Icon, title, body }) => (
              <section
                key={title}
                className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-6"
              >
                <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-rose-400" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{body}</p>
              </section>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6">Our principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {principles.map(({ icon: Icon, title, body, href, cta }) => (
              <section
                key={title}
                className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-6 flex flex-col"
              >
                <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-rose-400" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed flex-1">{body}</p>
                <Link
                  href={href}
                  className="inline-flex items-center gap-1.5 text-sm text-rose-400 hover:text-rose-300 transition-colors mt-4 group"
                >
                  {cta}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </section>
            ))}
          </div>
        </div>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-rose-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white">Where we are today</h2>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed mb-4">
            Silker AI is an independent company, founded in 2024 and{' '}
            <span className="text-white">made in Poland 🇵🇱</span>. We are early - we are currently onboarding
            our first design partners and building closely alongside them.
          </p>
          <p className="text-sm text-neutral-400 leading-relaxed">
            We would rather be honest about our stage than inflate it. If you are evaluating runtime security
            and want to shape the product with us, we want to hear from you.
          </p>
        </section>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Get started in minutes</h2>
          <p className="text-sm text-neutral-400 mb-6 max-w-xl mx-auto">
            One npm install and zero-config defaults. Read the docs or create a free account.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/docs"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-none border border-neutral-700 text-white text-sm font-medium hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors w-full sm:w-auto"
            >
              Read the docs
            </Link>
            <a
              href={platformLink.register()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors w-full sm:w-auto"
            >
              Create free account
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
